import api from '../services/api';
import {useQuery} from "react-query";
import queryClient from "../services/queryClient";
import {useEffect, useMemo, useState} from "react";
import {matchPath, useHistory, useLocation} from "react-router-dom";
import {useRecoilState, useSetRecoilState} from "recoil";
import preferenceState, {activeLocaleState, activeRegionState} from "../state/preferenceState";
import {useTranslation} from "react-i18next";

/*
 * Regions
 */
const fetchRegions = () => {
    return api
    .get(`/regions`)
    .then(response => response.data && response.data.data);
};

export const useFetchRegions = (options = {}) => {
    return useQuery('regions', fetchRegions, {
        staleTime: Infinity,
        cacheTime: Infinity,
        ...options,
    });
};

export const useRegion = () => {
    const setActive = useSetRecoilState(activeRegionState);
    const location = useLocation();
    const regions = useFetchRegions();

    const regex = `(${regions.data?.map(i => i.prefix)?.join('|') || ''})?`;

    const path = matchPath(location.pathname,  {path:`/:region${regex}`});
    const active = path && regions.data?.find(i => i.prefix === path?.params?.region) || null;

    return {
        regex,
        data: regions.data,
        active,
        setActive,
        createHref: (to = '/') => {
            return `/${active ? active.prefix + '/' : ''}${to.replace(/^\/|\/$/g, '')}`;
        },
        urlForRegion: (region) => {
            const url = location.pathname + (location.search || '');
            const prefix = region?.prefix || region;

            if(!prefix) {
                return active?.prefix ? url.replace(active.prefix, '').replace(/^\/|\/$/g, '') : url;
            }

            return !url.includes(active?.prefix) ? `/${prefix + url}` : url.replace(active?.prefix, prefix);
        }
    };
}

export const useSuggestRegion = () => {
    const history = useHistory();
    const [preference, setPreference] = useRecoilState(preferenceState);
    const {active: activeRegion, data: regions} = useRegion();

    const {i18n} = useTranslation();

    useEffect(() => {
        (async () => {
            const state = {...preference};
            if(state.country === false) {
                const data = await api.get('/geolocation', {timeout: 1000})
                .then(res => res?.data?.data || {})
                .catch(() => {});

                state.country = data?.iso_code || null;
            }

            if(state.region !== null) {
                state.region = state.region || regions?.find(i => i.country && i.country.iso_code === state?.country)?.prefix || null;

                if(
                  ['', '/'].includes(history?.location?.pathname || '/')
                  && !activeRegion
                  && state.region
                ) {
                    history.push(`/${state.region}/`);
                }
            }

            setPreference(old => {
                return {
                    ...old,
                    ...state,
                }
            });
        })();
    }, []);

    useEffect(() => {
        i18n.changeLanguage(preference.locale || activeRegion?.locale);

        if(activeRegion?.prefix) {
            setPreference(old => ({
                ...old,
                region: activeRegion.prefix,
                locale: old.locale || activeRegion.locale,
            }));
        }
    }, [activeRegion?.prefix, preference.locale]);


    return preference.region !== false;
}

/*
 * Locales
 */
const fetchLocales = () => {
    return api
    .get(`/locales`)
    .then(response => response.data && response.data.data);
};

export const useFetchLocales = (options = {}) => {
    return useQuery('locales', fetchLocales, {
        staleTime: Infinity,
        ...options,
    });
};

export const useLocale = () => {
    const {active: activeRegion} = useRegion();
    const locales = useFetchLocales();

    const [activeLocale, setActiveLocale] = useRecoilState(activeLocaleState);

    const active = locales.data?.find(i => i.id == (activeLocale || activeRegion?.locale || 'en'));

    return {
        data: locales.data,
        active,
        set: (locale) => {
            const lang = locale?.id || locale;
            setActiveLocale(lang);
        }
    }
}

/*
 * Currencies
 */
const fetchCurrencies = () => {
    return api
    .get(`/currencies`)
    .then(response => response.data && response.data.data);
};

export const useFetchCurrencies = (options = {}) => {
    return useQuery('currencies', fetchCurrencies, {
        staleTime: Infinity,
        ...options,
    });
};

export const useCurrency = () => {
    const {active: activeRegion} = useRegion();
    const currencies = useFetchCurrencies();

    return {
        data: currencies.data,
        active: currencies.data?.find(i => i.id === activeRegion?.currency),
    }
}

export const useApproxValue = () => {
    const {active: activeCurrency} = useCurrency();

    return useMemo(() => ({
        convert: (value, currency) => {
            if (!activeCurrency?.id || currency?.id === activeCurrency?.id) {
                return null;
            }

            const conversion = activeCurrency?.rates?.[currency?.code];

            if (conversion) {
                return Math.round(1 / conversion * value, 2).toString();
            } else {
                return null;
            }
        },
        currency: activeCurrency,
    }), [activeCurrency?.id]);
}

/*
 * Theatron Blocks
 */

const fetchTheatronBlocks = (params) => api.get(`/theatron/blocks`, {params}).then(res => res?.data?.data);

export const useFetchTheatronBlocks = (options = {}) => {
    const {active: activeRegion} = useRegion();
    const {active: activeLocale} = useLocale();

    const params = {
        region: activeRegion?.id || undefined,
        locale: activeLocale?.id || undefined,
    };

    return useQuery(['theatron.blocks', params], () => fetchTheatronBlocks(params), {
        staleTime: Infinity,
        ...options,
    });
}


/*
 * Theatron
 */
export const useHasListing = () => {
    const theatron = useFetchTheatron();

    useEffect(() => {
        if(theatron.data && !theatron.data?.modules?.listing) {
            window.location = theatron.data?.redirect_url || 'https://www.cloudtix.co';
        }
    }, [theatron.data?.modules?.listing]);

    return !!theatron.data?.modules?.listing;
}

export const fetchTheatron = () => api.get(`/theatron`).then(res => {
    const data = res?.data?.data;

    if(data?.regions) {
        queryClient.setQueryData('regions', data.regions, {
            staleTime: Infinity,
            cacheTime: Infinity,
        });
    }
    if(data?.locales) {
        queryClient.setQueryData('locales', data.locales, {
            staleTime: Infinity,
            cacheTime: Infinity,
        });
    }
    if(data?.currencies) {
        queryClient.setQueryData('currencies', data.currencies, {
            staleTime: Infinity,
            cacheTime: Infinity,
        });
    }

    return data;
});

const useFetchTheatron = (options = {}) => {
    return useQuery('theatron', () => fetchTheatron(), {
        staleTime: Infinity,
        cacheTime: Infinity,
        ...options,
    });
}

export default useFetchTheatron;
